<template>
	<button
		:disabled="disabled"
		class="d-flex align-center justify-center transition-all rounded-2 cursor-pointer state-styles"
		:class="design.classes"
		:style="design.styles"
		:type="isSubmit ? 'submit' : 'button'"
		@click.stop="$emit('click', $event)"
	>
		<NewIcon
			v-if="icon && useNewIcon"
			:radius="iconSize"
			:rotate="rotateIcon"
			:icon="icon"
			:class="{ 'mr-1': !onlyIcon }"
		/>
		<Icon
			v-else-if="icon"
			:radius="iconSize"
			:rotate="rotateIcon"
			:icon="icon"
			:class="{ 'mr-1': !onlyIcon }"
		/>
		<span v-if="!onlyIcon" :class="textTypography" style="white-space: nowrap">
			<slot />
		</span>
	</button>
</template>

<script setup lang="ts">
	import { computed } from 'vue'
	import Icon from './Icon.vue'
	import NewIcon from './NewIcon.vue'
	import {
		ButtonColor,
		ButtonSize,
		ButtonStyle,
		ButtonType,
	} from '~/common/passporter-styles/designSystem/button'

	interface Props {
		type?: ButtonType
		color?: ButtonColor
		size?: ButtonSize
		disabled?: boolean
		icon?: string
		onlyIcon?: boolean
		isSubmit?: boolean
		iconSize?: string
		rotateIcon?: '' | 'right' | 'down' | 'left'
		useNewIcon?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		type: 'filled',
		color: 'primary',
		size: 'md',
		disabled: false,
		icon: '',
		iconSize: '24px',
		rotateIcon: '',
		useNewIcon: false,
	})

	defineEmits(['click'])

	const design = computed(() => {
		const button = new ButtonStyle(props.type, props.color, props.size, props.onlyIcon)
		const classes = button.getClasses()
		const styles = button.getStyles()

		return { classes, styles }
	})

	const textTypography = computed(() => {
		if (props.size === 'sm' || props.size === 'xs') return 'text-label-s'
		if (props.size === 'md') return 'text-label-m'
		return 'text-label-l'
	})
</script>

<style lang="scss" scoped>
	.state-styles {
		height: var(--height);
		width: var(--width);

		&:hover {
			background-color: var(--bg-hover);
			border-color: var(--border-hover);
		}
		&:focus {
			background-color: var(--bg-focus);
			border-color: var(--border-focus);
		}
		&:active {
			background-color: var(--bg-active);
			border-color: var(--border-active);
		}
		&:disabled {
			background-color: var(--bg-disabled);
			color: var(--text-disabled);
		}
	}
</style>
